/**
 * Region edit popover (click on a region body to open).
 *
 * Inputs: label, mode (skip/include/repeat/reverse/fwd-back-fwd), repeat-count, speed.
 * Buttons: Löschen, Abbrechen, Speichern.
 */
import { escAttr } from '../core/utils.js';
import { srvUpdate, srvDelete } from '../core/regions.js';
import { visibleDur, leftBoundT } from '../core/timeline.js';

export function createPopover() {
  const el = document.createElement('div');
  el.className = 'zap-pe-popover';
  el.hidden = true;
  return el;
}

export function openRegionPopover(opts) {
  const {
    popover, stage, strip, regions, idx, timeline,
    rawPath, writeCache, renderStrip,
  } = opts;
  const r = regions[idx];
  if (!r) return;

  popover.hidden = false;
  popover.innerHTML = `
    <h4 style="margin:0 0 8px;font-size:13px;">Region bearbeiten</h4>
    <label>Label <input id="popLabel" type="text" maxlength="120" value="${escAttr(r.label || '')}"></label>
    <label>Modus
      <select id="popMode">
        <option value="skip"          ${r.mode==='skip'?'selected':''}>⏭ Überspringen</option>
        <option value="include"       ${r.mode==='include'?'selected':''}>▶ Spielen (nur Marker)</option>
        <option value="repeat"        ${r.mode==='repeat'?'selected':''}>🔁 N× wiederholen</option>
        <option value="reverse"       ${r.mode==='reverse'?'selected':''}>◀ Rückwärts</option>
        <option value="fwd-back-fwd"  ${r.mode==='fwd-back-fwd'?'selected':''}>▶◀▶ Vor-Zurück-Vor</option>
      </select>
    </label>
    <label id="popRepeatLabel">Anzahl Wiederholungen
      <input id="popRepeat" type="number" min="1" max="10" value="${r.repeat_count || 3}">
    </label>
    <label>Geschwindigkeit
      <input id="popSpeed" type="number" min="0.25" max="3" step="0.25" value="${r.speed != null ? r.speed : 1}">
      <span style="color:rgba(255,255,255,.5);font-size:11px;">1 = normal, 0.5 = halb, 2 = doppelt</span>
    </label>
    <div class="row">
      <button id="popDelete" style="margin-right:auto;background:rgba(220,60,60,.3);border-color:rgba(220,60,60,.5);">Löschen</button>
      <button id="popCancel">Abbrechen</button>
      <button id="popSave" class="primary">Speichern</button>
    </div>
  `;

  // Position above the region
  const rect  = stage.getBoundingClientRect();
  const sRect = strip.getBoundingClientRect();
  const xPct  = ((r.start + r.end) / 2 - leftBoundT(timeline)) / visibleDur(timeline);
  const popX  = sRect.left - rect.left + sRect.width * Math.max(0, Math.min(1, xPct));
  popover.style.left   = Math.max(8, Math.min(rect.width - 280, popX - 130)) + 'px';
  popover.style.bottom = (rect.bottom - sRect.top + 8) + 'px';
  popover.style.top    = '';

  const updateRepeatVisible = () => {
    const m = popover.querySelector('#popMode').value;
    popover.querySelector('#popRepeatLabel').style.display =
      (m === 'repeat' || m === 'fwd-back-fwd') ? '' : 'none';
  };
  updateRepeatVisible();

  popover.querySelector('#popMode').addEventListener('change', updateRepeatVisible);
  popover.querySelector('#popCancel').addEventListener('click', () => { popover.hidden = true; });
  popover.querySelector('#popDelete').addEventListener('click', () => {
    if (r.id) srvDelete(r.id);
    regions.splice(idx, 1);
    writeCache(); renderStrip();
    popover.hidden = true;
  });
  popover.querySelector('#popSave').addEventListener('click', () => {
    r.label        = popover.querySelector('#popLabel').value.slice(0, 120);
    r.mode         = popover.querySelector('#popMode').value;
    r.repeat_count = (r.mode === 'repeat' || r.mode === 'fwd-back-fwd')
      ? parseInt(popover.querySelector('#popRepeat').value) || 1
      : null;
    const sp = parseFloat(popover.querySelector('#popSpeed').value);
    r.speed = (isFinite(sp) && Math.abs(sp - 1) > 0.001) ? sp : null;
    writeCache(); srvUpdate(rawPath, r); renderStrip();
    popover.hidden = true;
  });
}

/** Close popover when clicking outside. Returns the listener so caller can detach. */
export function attachPopoverOutsideClick(popover) {
  const listener = (e) => {
    if (popover.hidden) return;
    if (e.target.closest('.zap-pe-popover')) return;
    if (e.target.closest('.zap-pe-region'))  return;
    popover.hidden = true;
  };
  document.addEventListener('click', listener);
  return () => document.removeEventListener('click', listener);
}
